import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { runAssignmentAutoMigration, manualLinkAndFixAssignment, listMyQuizzes } from '../utils/migrateUndefinedAssignments';

const PRESET_TEST_STUDENTS = [
  { firstName: 'Test', lastName: 'Student1', email: 'test.student1@example.com', studentId: 'TS001' },
  { firstName: 'Test', lastName: 'Student2', email: 'test.student2@example.com', studentId: 'TS002' },
  { firstName: 'Test', lastName: 'Student3', email: 'test.student3@example.com', studentId: 'TS003' },
];

export default function DevPanel() {
  const { user, userDoc } = useAuth();
  const subscription = useSubscription();
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [result, setResult] = useState(null);
  const [missing, setMissing] = useState([]);
  const [quizzes, setQuizzes] = useState([]);
  const [linking, setLinking] = useState({ assignmentId: null, quizId: '' });

  // Only show dev panel in development or for tester accounts
  const shouldShowPanel = process.env.NODE_ENV === 'development' || userDoc?.isTesterAccount;

  if (!shouldShowPanel) return null;

  const handleToggleTesterAccount = async () => {
    setLoading(true);
    try {
      await updateDoc(doc(db, 'users', user.uid), {
        isTesterAccount: !userDoc?.isTesterAccount
      });
      setMessage(`Tester account ${!userDoc?.isTesterAccount ? 'enabled' : 'disabled'}`);
    } catch (error) {
      setMessage('Error updating tester status: ' + error.message);
    }
    setLoading(false);
  };

  const handleMigrateAssignments = async () => {
    setLoading(true);
    setMessage('🔧 Running assignment migration...');
    setResult(null);
    setMissing([]);

    try {
      const res = await runAssignmentAutoMigration();
      setResult(res);
      setMissing(res.missingQuiz || []);

      if ((res.missingQuiz || []).length) {
        const qs = await listMyQuizzes({ limit: 50 });
        setQuizzes(qs);
      }

      setMessage(`✅ Migration complete! Fixed: ${res.fixed}, Skipped: ${res.skipped}, Errors: ${res.errors}`);
    } catch (error) {
      setResult({ fixed: 0, skipped: 0, errors: 1, message: error.message });
      setMessage('❌ Migration error: ' + error.message);
    }
    setLoading(false);
  };

  const handleManualLinkFix = async () => {
    if (!linking.assignmentId || !linking.quizId) return;
    setLoading(true);

    try {
      const res = await manualLinkAndFixAssignment({
        assignmentId: linking.assignmentId,
        quizId: linking.quizId
      });

      // Remove from missing list
      setMissing(prev => prev.filter(a => a.id !== linking.assignmentId));
      setResult(res);
      setLinking({ assignmentId: null, quizId: '' });
      setMessage(`✅ Manually linked and fixed: ${res.title}`);
    } catch (error) {
      setMessage('❌ Manual link error: ' + error.message);
    }
    setLoading(false);
  };

  const handleCreateTestStudents = async () => {
    setLoading(true);
    setMessage('Creating test students...');

    try {
      // Import required Firebase functions
      const {
        collection, query, where, getDocs, getDoc, doc,
        setDoc, deleteDoc, updateDoc, serverTimestamp,
        runTransaction, arrayUnion, writeBatch, increment
      } = await import('firebase/firestore');
      const { createUserWithEmailAndPassword, signOut } = await import('firebase/auth');
      const { getAuth } = await import('firebase/auth');
      const { initializeApp, deleteApp } = await import('firebase/app');
      const { app, db, auth } = await import('../firebase');

      // Student creation logic
      const createStudentAsTeacher = async ({ firstName, lastName, email, password, studentId }) => {
        const teacher = auth.currentUser;
        if (!teacher) throw new Error("Not signed in");

        const provisioningApp = initializeApp(app.options, "student-provisioning-" + Date.now());
        const provisioningAuth = getAuth(provisioningApp);

        try {
          const cred = await createUserWithEmailAndPassword(provisioningAuth, email, password);
          const studentUid = cred.user.uid;

          const studentDoc = {
            firstName,
            lastName,
            displayName: `${firstName} ${lastName}`,
            email,
            studentId,
            role: "student",
            status: "active",
            isActive: true,
            teacherId: teacher.uid,
            managedBy: [teacher.uid],
            examHistory: [],
            violations: [],
            schoolId: null,
            createdAt: serverTimestamp(),
          };

          const batch = writeBatch(db);
          batch.set(doc(db, "users", studentUid), studentDoc);
          batch.update(doc(db, "users", teacher.uid), {
            managedStudents: arrayUnion(studentUid),
            studentsCreated: increment(1),
          });
          await batch.commit();

          return { success: true, studentUid, email };

        } finally {
          await signOut(provisioningAuth).catch(() => {});
          await deleteApp(provisioningApp).catch(() => {});
        }
      };

      for (const student of PRESET_TEST_STUDENTS) {
        await createStudentAsTeacher({
          ...student,
          password: import.meta.env.VITE_TEST_PASSWORD || 'TestPass123!'
        });
      }

      setMessage('✅ Test students created successfully!');
    } catch (error) {
      setMessage('Error creating test students: ' + error.message);
    }
    setLoading(false);
  };

  return (
    <div className="fixed bottom-4 right-4 z-50">
      {/* Toggle Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="bg-purple-600 hover:bg-purple-700 text-white p-3 rounded-full shadow-lg transition-colors"
        title="Dev Panel"
      >
        🛠️
      </button>

      {/* Dev Panel */}
      {isOpen && (
        <div className="absolute bottom-16 right-0 bg-white rounded-lg shadow-xl border border-gray-200 p-4 w-80">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-800">🛠️ Dev Panel</h3>
            <button
              onClick={() => setIsOpen(false)}
              className="text-gray-500 hover:text-gray-700"
            >
              ✕
            </button>
          </div>

          {/* Account Status */}
          <div className="mb-4 p-3 bg-gray-50 rounded-lg">
            <div className="text-sm">
              <div><strong>Email:</strong> {user?.email}</div>
              <div><strong>Role:</strong> {userDoc?.role}</div>
              <div><strong>Tester Account:</strong> {userDoc?.isTesterAccount ? '✅ Yes' : '❌ No'}</div>
              <div><strong>Subscription Access:</strong> {subscription.checkFeatureAccess().allowed ? '✅' : '❌'}</div>
            </div>
          </div>

          {/* Actions */}
          <div className="space-y-3">
            <button
              onClick={handleToggleTesterAccount}
              disabled={loading}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white px-4 py-2 rounded-lg font-medium transition-colors"
            >
              {loading ? '⏳' : userDoc?.isTesterAccount ? 'Disable' : 'Enable'} Tester Account
            </button>

            <button
              onClick={handleMigrateAssignments}
              disabled={loading}
              className="w-full bg-orange-600 hover:bg-orange-700 disabled:bg-orange-400 text-white px-4 py-2 rounded-lg font-medium transition-colors"
            >
              {loading ? '⏳ Migrating...' : '🔧 Fix Undefined Assignments'}
            </button>

            <button
              onClick={handleCreateTestStudents}
              disabled={loading || !userDoc?.isTesterAccount}
              className="w-full bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white px-4 py-2 rounded-lg font-medium transition-colors"
              title={!userDoc?.isTesterAccount ? 'Enable tester account first' : ''}
            >
              {loading ? '⏳ Creating...' : '👥 Create Test Students'}
            </button>

            <div className="text-xs text-gray-600">
              Creates 3 test students with password: <code className="bg-gray-200 px-1 rounded">{import.meta.env.VITE_TEST_PASSWORD || 'TestPass123!'}</code>
            </div>
          </div>

          {/* Migration Results */}
          {result && (
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm">
              <div className="font-semibold mb-1">✅ Migration Complete!</div>
              <div>Fixed: {result.fixed ?? 0}, Skipped: {result.skipped ?? 0}, Errors: {result.errors ?? 0}</div>
              {result.message && <div className="text-red-600 mt-1">Note: {result.message}</div>}
            </div>
          )}

          {/* Manual Link & Fix UI */}
          {missing.length > 0 && (
            <div className="mt-4 p-3 border border-orange-300 rounded-lg bg-orange-50">
              <div className="font-semibold mb-2 text-sm">⚠️ Manual Link Needed ({missing.length})</div>
              <div className="text-xs text-gray-700 mb-3">
                These assignments have no <code>quizId</code>. Select the correct quiz and click <strong>Link & Fix</strong>.
              </div>

              <div className="space-y-3 max-h-64 overflow-auto">
                {missing.map(a => (
                  <div key={a.id} className="p-2 rounded-lg bg-white border border-gray-200">
                    <div className="text-xs text-gray-500">Assignment ID: {a.id.substring(0, 8)}...</div>
                    <div className="text-sm mt-1">
                      <strong>Current:</strong> {a.title || "undefined Assignment"}
                    </div>
                    <div className="text-xs text-gray-600">
                      Questions: {a.questionCount ?? 0}
                    </div>

                    <div className="mt-2 flex gap-2">
                      <select
                        className="flex-1 text-sm border border-gray-300 rounded px-2 py-1"
                        value={linking.assignmentId === a.id ? (linking.quizId || '') : ''}
                        onChange={(e) => setLinking({ assignmentId: a.id, quizId: e.target.value })}
                      >
                        <option value="">Select quiz…</option>
                        {quizzes.map(q => (
                          <option key={q.id} value={q.id}>
                            {q.title || "Untitled Quiz"} ({Array.isArray(q.questions) ? q.questions.length : (q.questionCount || 0)} q)
                          </option>
                        ))}
                      </select>
                      <button
                        className="bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white px-3 py-1 rounded text-sm font-medium"
                        disabled={loading || !(linking.assignmentId === a.id && linking.quizId)}
                        onClick={handleManualLinkFix}
                      >
                        Link & Fix
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Message */}
          {message && (
            <div className="mt-3 p-2 bg-blue-50 border border-blue-200 rounded text-sm text-blue-800">
              {message}
            </div>
          )}

          {/* Quick Links for Testing */}
          <div className="mt-4 pt-3 border-t border-gray-200">
            <div className="text-sm font-medium text-gray-700 mb-2">🔗 Quick Links</div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <a href="/students" className="bg-gray-100 hover:bg-gray-200 px-2 py-1 rounded text-center transition-colors">Students</a>
              <a href="/dashboard" className="bg-gray-100 hover:bg-gray-200 px-2 py-1 rounded text-center transition-colors">Dashboard</a>
              <a href="/assignments" className="bg-gray-100 hover:bg-gray-200 px-2 py-1 rounded text-center transition-colors">Assignments</a>
              <a href="/ai-builder" className="bg-gray-100 hover:bg-gray-200 px-2 py-1 rounded text-center transition-colors">AI Builder</a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}